import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  People as PeopleIcon,
  ShoppingCart as OrderIcon,
  Receipt as InvoiceIcon,
  Payment as PaymentIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  AccountBalance as AccountBalanceIcon,
  LocalShipping as ShipmentIcon,
  Warning as LowStockIcon,
  Business as SupplierIcon,
  ListAlt as SupplierOrderIcon,
  Description as StatementIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

const Layout: React.FC = () => {
  const { user, logout, hasPermission, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [paymentsExpanded, setPaymentsExpanded] = useState(true); // Default expanded
  const [productsExpanded, setProductsExpanded] = useState(true); // Default expanded

  // Auto-collapse on tablet/mobile devices
  useEffect(() => {
    if (isTablet && !isMobile) {
      setDesktopCollapsed(true);
    } else if (!isTablet && !isMobile) {
      setDesktopCollapsed(false);
    }
  }, [isTablet, isMobile]);
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopToggle = () => {
    setDesktopCollapsed(!desktopCollapsed);
  };
  
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      navigate('/login');
    }
  };

  const handleProfile = () => {
    navigate('/settings');
    handleMenuClose();
  };
  
  const allSimpleMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/', permission: 'dashboard.read' },
    { text: 'Categories', icon: <CategoryIcon />, path: '/categories', permission: 'categories.create' },
    { text: 'Customers', icon: <PeopleIcon />, path: '/customers', permission: 'customers.read' },
    { text: 'Orders', icon: <OrderIcon />, path: '/orders', permission: 'orders.read' },
    { text: 'Invoices', icon: <InvoiceIcon />, path: '/invoices', permission: 'invoices.read' },
    { text: 'Statements', icon: <StatementIcon />, path: '/statements', permission: 'customer_statements.read' },
    { text: 'Shipments', icon: <ShipmentIcon />, path: '/shipments', permission: 'shipments.read' },
    { text: 'Suppliers', icon: <SupplierIcon />, path: '/suppliers', permission: 'suppliers.read' },
    { text: 'Supplier Orders', icon: <SupplierOrderIcon />, path: '/supplier-orders', permission: 'supplier_orders.read' },
  ];

  const settingsMenuItem = { text: 'Settings', icon: <SettingsIcon />, path: '/settings', permission: null };

  // Filter menu items based on permissions (only super admin and admin bypass permission checks)
  const simpleMenuItems = allSimpleMenuItems.filter(item => 
    !item.permission || hasPermission(item.permission) || hasRole(['super_admin', 'admin'])
  );

  const paymentsSubmenu = [
    ...(hasPermission('payments.read') || hasRole(['super_admin', 'admin']) ?
      [{ text: 'Payment Records', path: '/payments' }] : []),
    ...(hasPermission('customer_balances.read') || hasRole(['super_admin', 'admin']) ?
      [{ text: 'Customer Balances', path: '/customer-balances' }] : []),
  ];

  const productsSubmenu = [
    { text: 'All Products', path: '/products', icon: <InventoryIcon /> },
    // Only show Low Stock to users with dashboard access (not shipment managers)
    ...(hasPermission('dashboard.read') || hasRole(['super_admin', 'admin']) ? 
      [{ text: 'Low Stock', path: '/products/low-stock', icon: <LowStockIcon /> }] : []
    ),
  ];

  // Check if payments section should be visible
  const showPayments = hasPermission('payments.read') || hasPermission('customer_balances.read') || hasRole(['super_admin', 'admin']);
  
  // Check if products section should be visible (only show if user can actually read products)
  const showProducts = hasPermission('products.read') || hasRole(['super_admin', 'admin']);

  // Combined for title lookup
  const allMenuItems = [
    ...simpleMenuItems,
    { text: 'All Products', icon: <InventoryIcon />, path: '/products' },
    { text: 'Low Stock', icon: <LowStockIcon />, path: '/products/low-stock' },
    { text: 'Payments', icon: <PaymentIcon />, path: '/payments' },
    { text: 'Customer Balances', icon: <AccountBalanceIcon />, path: '/customer-balances' },
    { text: 'Statements', icon: <StatementIcon />, path: '/statements' },
    { text: 'Supplier Orders', icon: <SupplierIcon />, path: '/supplier-orders' },
    { text: 'Suppliers', icon: <SupplierIcon />, path: '/suppliers' },
    settingsMenuItem,
  ];
  
  const currentDrawerWidth = isMobile ? drawerWidth : (desktopCollapsed ? collapsedDrawerWidth : drawerWidth);

  const drawer = (isCollapsed = false) => (
    <div>
      <Toolbar sx={{ justifyContent: isCollapsed ? 'center' : 'space-between', py: 2, minHeight: '64px !important' }}>
        {!isCollapsed && (
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold' }}>
            WMS PRO
          </Typography>
        )}
        {!isMobile && (
          <IconButton
            onClick={handleDesktopToggle}
            sx={{ 
              ml: isCollapsed ? 0 : 'auto',
              color: 'text.secondary'
            }}
          >
            {desktopCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        )}
      </Toolbar>
      <Divider />
      <List>
        {/* Render Dashboard first */}
        {simpleMenuItems.slice(0, 1).map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                if (isMobile) {
                  setMobileOpen(false);
                }
              }}
              sx={{
                minHeight: 48,
                justifyContent: isCollapsed ? 'center' : 'initial',
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: isCollapsed ? 'auto' : 3,
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!isCollapsed && <ListItemText primary={item.text} />}
            </ListItemButton>
          </ListItem>
        ))}
        
        {/* Products Expandable Menu - Second position */}
        {showProducts && (
          <>
            <ListItem disablePadding>
              <ListItemButton
                selected={location.pathname.startsWith('/products')}
                onClick={() => {
                  if (isCollapsed) {
                    navigate('/products');
                    if (isMobile) {
                      setMobileOpen(false);
                    }
                  } else {
                    setProductsExpanded(!productsExpanded);
                  }
                }}
                sx={{
                  minHeight: 48,
                  justifyContent: isCollapsed ? 'center' : 'initial',
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isCollapsed ? 'auto' : 3,
                    justifyContent: 'center',
                  }}
                >
                  <InventoryIcon />
                </ListItemIcon>
                {!isCollapsed && (
                  <>
                    <ListItemText primary="Products" />
                    {productsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </>
                )}
              </ListItemButton>
            </ListItem>
            
            {/* Products Submenu */}
            {!isCollapsed && (
              <Collapse in={productsExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {productsSubmenu.map((subItem) => (
                    <ListItem key={subItem.text} disablePadding>
                      <ListItemButton
                        selected={location.pathname === subItem.path}
                        onClick={() => {
                          navigate(subItem.path);
                          if (isMobile) {
                            setMobileOpen(false);
                          }
                        }}
                        sx={{
                          pl: 4,
                          minHeight: 48,
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: 3,
                            justifyContent: 'center',
                          }}
                        >
                          {subItem.icon}
                        </ListItemIcon>
                        <ListItemText primary={subItem.text} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </>
        )}
        
        {/* Render remaining menu items (from Categories onward) */}
        {simpleMenuItems.slice(1).map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                if (isMobile) {
                  setMobileOpen(false);
                }
              }}
              sx={{
                minHeight: 48,
                justifyContent: isCollapsed ? 'center' : 'initial',
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: isCollapsed ? 'auto' : 3,
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!isCollapsed && <ListItemText primary={item.text} />}
            </ListItemButton>
          </ListItem>
        ))}
        
        {/* Payments Expandable Menu */}
        {showPayments && (
          <>
            <ListItem disablePadding>
              <ListItemButton
                selected={location.pathname.startsWith('/payments') || location.pathname.startsWith('/customer-balances')}
                onClick={() => {
                  if (isCollapsed) {
                    navigate('/payments');
                    if (isMobile) {
                      setMobileOpen(false);
                    }
                  } else {
                    setPaymentsExpanded(!paymentsExpanded);
                  }
                }}
                sx={{
                  minHeight: 48,
                  justifyContent: isCollapsed ? 'center' : 'initial',
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isCollapsed ? 'auto' : 3,
                    justifyContent: 'center',
                  }}
                >
                  <PaymentIcon />
                </ListItemIcon>
                {!isCollapsed && (
                  <>
                    <ListItemText primary="Payments" />
                    {paymentsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </>
                )}
              </ListItemButton>
            </ListItem>
            
            {/* Payments Submenu */}
            {!isCollapsed && (
              <Collapse in={paymentsExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {paymentsSubmenu.map((subItem) => (
                    <ListItem key={subItem.text} disablePadding>
                      <ListItemButton
                        selected={location.pathname === subItem.path}
                        onClick={() => {
                          navigate(subItem.path);
                          if (isMobile) {
                            setMobileOpen(false);
                          }
                        }}
                        sx={{
                          pl: 4,
                          minHeight: 48,
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: 3,
                            justifyContent: 'center',
                          }}
                        >
                          {subItem.path === '/payments' ? <PaymentIcon /> : <AccountBalanceIcon />}
                        </ListItemIcon>
                        <ListItemText primary={subItem.text} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </>
        )}
        
        {/* Settings Menu - Last position */}
        <ListItem disablePadding>
          <ListItemButton
            selected={location.pathname === settingsMenuItem.path}
            onClick={() => {
              navigate(settingsMenuItem.path);
              if (isMobile) {
                setMobileOpen(false);
              }
            }}
            sx={{
              minHeight: 48,
              justifyContent: isCollapsed ? 'center' : 'initial',
              px: 2.5,
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: isCollapsed ? 'auto' : 3,
                justifyContent: 'center',
              }}
            >
              {settingsMenuItem.icon}
            </ListItemIcon>
            {!isCollapsed && <ListItemText primary={settingsMenuItem.text} />}
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );
  
  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {allMenuItems.find((item) => item.path === location.pathname)?.text || 'Warehouse Management System'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={handleMenuOpen}
              color="inherit"
              edge="end"
              aria-label="user menu"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem disabled>
                <Typography variant="body2">
                  {user?.firstName && user?.lastName 
                    ? `${user.firstName} ${user.lastName}` 
                    : 'User'
                  }
                </Typography>
              </MenuItem>
              <MenuItem disabled>
                <Typography variant="body2" color="textSecondary">
                  {user?.email || 'user@example.com'}
                </Typography>
              </MenuItem>
              <MenuItem disabled>
                <Typography variant="caption" color="textSecondary">
                  Role: {user?.role || 'Unknown'}
                </Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleProfile}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Profile & Settings" />
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: currentDrawerWidth }, flexShrink: { md: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer(false)}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: currentDrawerWidth,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer(desktopCollapsed)}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
